/* eslint no-useless-escape: "off" */
/* eslint-disable @typescript-eslint/camelcase */
import get from 'lodash/get'
import has from 'lodash/has'
import axios from 'axios'

import CONSTANTS from '~/constants'
import { SEARCH_TYPE_PARAM } from '~/modules/search/utils'

export function unformatDSL(dsl) {
  let dslObj = {}
  if (typeof dsl === 'string') {
    try {
      dslObj = JSON.parse(dsl)
    } catch (e) {
      return []
    }
  } else if (typeof dsl === 'object') {
    dslObj = dsl
  } else {
    return []
  }

  return get(dslObj, 'query.bool.must', []).reduce((table, i) => {
    const dslType = Object.keys(i)[0]
    switch (dslType) {
      case 'simple_query_string':
        if (has(i, 'simple_query_string.query')) {
          table.push({
            path: 'searchBar',
            value: i.simple_query_string.query,
          })
        }
        break
      case 'bool': {
        const deepDive = i.bool.should[1].nested
        unformatDSL(deepDive).forEach((i) => table.push(i))
        break
      }
      default: {
        const path = Object.keys(i[dslType])[0]
        let value = i[dslType][path]
        if (path === 'source.system' && dslType === 'terms') {
          // [0]: user-entered term
          // [1]: additional term
          value = value[0]
        }
        table.push({
          path: path,
          value: value,
        })
      }
    }
    return table
  }, [])
}

export function formatDSL(params, website) {
  if (!Array.isArray(params) || !website) return ''
  // This is not human readable. Please refer to these two documents for understanding
  // ES DSL: https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl.html
  // Multisite query Doc by Greg E.:https://arcpublishing.github.io/docs/examples/content-api/multisite#query-the-content-api-within-a-website
  // init query
  const query = {
    query: {
      bool: {
        must: [],
      },
    },
  }
  params.map((i) => {
    if (!i.value) return
    const queryMap = {
      // using SHOULD query works the same as the legacy OR for taxonomy.site._id & taxonomy.sections._id
      type: {
        term: {
          type: i.value,
        },
      },
      'taxonomy.sections._id': {
        bool: {
          should: [
            {
              term: {
                'taxonomy.sites._id': i.value,
              },
            },
            {
              nested: {
                path: 'taxonomy.sections',
                query: {
                  bool: {
                    must: [
                      {
                        term: {
                          'taxonomy.sections._id': i.value,
                        },
                      },
                      {
                        term: {
                          'taxonomy.sections._website': website,
                        },
                      },
                    ],
                  },
                },
              },
            },
          ],
        },
      },
      searchBar: {
        simple_query_string: {
          query: i.value,
          fields: [
            'headlines.basic^10',
            'subheadlines.basic^3',
            'slug^3',
            'content_elements.content^2',
            'content_elements.items.content^2',
            'source.source_id',
            'owner.name',
            'editor_note',
            'credits.by.name',
            'taxonomy.tags.text',
            'promo_items.basic.headlines.basic',
            'promo_items.basic.subheadlines.basic',
            'promo_items.basic.description.basic',
            'planning.budget_line',
            'label.basic.text',
            'corrections.text',
          ],
          flags: 'ALL|AND|PREFIX',
        },
      }, // This needs to be more strict but it works for now
      'revision.published': {
        term: {
          'revision.published': i.value,
        },
      },
      'credits.by.name': {
        match_phrase_prefix: {
          'credits.by.name': i.value,
        },
      },
      created_date: {
        range: {
          created_date: i.value,
        },
      },
      publish_date: {
        range: {
          publish_date: i.value,
        },
      },
      last_updated_date: {
        range: {
          last_updated_date: i.value,
        },
      },
      'headlines.basic': {
        match_phrase: {
          'headlines.basic': i.value,
        },
      },
      'source.system': {
        match_phrase: {
          'source.system': i.value,
        },
      },
      subtype: {
        match_phrase: {
          subtype: i.value,
        },
      },
      'workflow.status_code': {
        terms: {
          'workflow.status_code': i.value,
        },
      },
    }
    // check if the query path exists and has a value
    if (queryMap[i.path]) {
      if (
        i.path === 'source.system' &&
        (i.value === 'composer' || i.value === 'ellipsis')
      ) {
        query.query.bool.must.push({
          terms: {
            // [0]: user-entered term - used to populate persisted searches in unformatDSL
            // [1]: additional term - ignored when creating persisted searches
            'source.system': [
              i.value,
              i.value === 'ellipsis' ? 'composer' : 'ellipsis',
            ],
          },
        })
      } else {
        query.query.bool.must.push(queryMap[i.path])
      }
    }
  })
  return query
}

function search($http, $q, $location) {
  let currentQs
  let currentSort
  let currentParams
  let currentPage = 0
  let currentWebsite
  // bad naming, this is really just the q= part
  const fetchSize = 20
  let results = []
  let status = ''

  function setResults(response) {
    const { data = {} } = response
    status = 'complete'
    results = data

    /**
     * Change the return to an object to standarize the
     * information handled, with this we can know when
     * process was sucessfully completed or not
     */
    return {
      results,
      status,
      error: false,
    }
  }

  function setResultsError(response = undefined) {
    results = []
    status = 'error'

    /**
     * Change the return to an object to standarize the
     * information handled, with this we can know when
     * process was sucessfully completed or not
     */
    return {
      results,
      status,
      error: true,
      response,
    }
  }

  async function fetchItems(params, sort, from, count, website, searchType) {
    try {
      const qs = []
      status = 'loading'
      if (website) qs.push('website=' + website)
      if (sort) qs.push('sort=' + sort)
      qs.push('from=' + from)
      qs.push('size=' + count)
      // elasticsearch DSL
      qs.push(
        'body=' + encodeURIComponent(JSON.stringify(formatDSL(params, website)))
      )
      currentQs = qs.join('&')
      $location.search(`${SEARCH_TYPE_PARAM}=${searchType}&${currentQs}`)
      const req = {
        method: 'GET',
        url: `${CONSTANTS.TRIDENT_ROUTE_COMPOSER_API}/v1/search/?` + currentQs,
        timeout: 100000,
      }
      /**
       * Changed function to async/await and handling the req with Axios
       *
       * With this, if something goes wrong in the API Call,
       * we can execute successfully the setResultError function.
       *
       * Previously it was always executing a HTTP 200 response
       */
      const response = await axios(req)
      return setResults(response)
    } catch (error) {
      return setResultsError(error.response)
    }
  }

  this.fetchNewSearch = async function (query, sort, website, searchType) {
    currentWebsite = website
    currentParams = query
    currentSort = sort
    currentPage = 0
    results = []
    return fetchItems(query, sort, 0, fetchSize, website, searchType)
  }

  this.fetchNextPage = function () {
    if (status === 'loading') return
    currentPage += 1
    const from = currentPage * fetchSize
    return fetchItems(
      currentParams,
      currentSort,
      from,
      fetchSize,
      currentWebsite
    )
  }

  this.getCurrentPage = () => currentPage

  this.getResults = function () {
    return results
  }

  this.getStatus = function () {
    return status
  }
}

export default ['$http', '$q', '$location', search]
